// --- BOTÓN WHATSAPP FLOTANTE + PANEL DE CONFIGURACIÓN ---
// Inyectado directamente en body para evitar interferencias con el editor.
import { store } from '../core/store.js';
import { registerPostRender } from '../core/render.js';
import { t } from '../utils/i18n.js';

const WA_SVG = `<svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.549 4.107 1.51 5.84L.057 23.54a.5.5 0 0 0 .603.601l5.7-1.452A11.943 11.943 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.823 9.823 0 0 1-5.006-1.369l-.36-.213-3.724.949.969-3.724-.234-.373A9.818 9.818 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/>
</svg>`;

// ─── PANEL ───────────────────────────────────────────────────────────────────

let panelEl = null;

// Panel de configuración del EDITOR (re-skin Nocturne, 14-10-2026): la carcasa de los popovers del editor
// (`.n3i-popover`: panel, radio 12, sombra del panel, padding 14, cabecera con icono, título 14/500 y ✕ de 28), el
// campo con la receta `.n3i-campo` y «Guardar» con `.n3i-btn-primary` (editor.css, cargado en el editor y en el
// creador: este panel solo se abre ahí). El logo de WhatsApp de la cabecera es el del botón flotante, en su color
// de marca (es una marca, no un acento). La ✕ se dibuja con CSS: este módulo entra en el bundle PÚBLICO
// (public-init.js) y `utils/icons-ph.js` no debe colarse ahí (test_barra_bloque, «el bundle público»). El botón
// flotante que ve el VISITANTE no cambia: su estilo es el de site.css.
// 280 de ancho: `site.css` centra el panel en la vista móvil con `left: calc(50% - 140px)`.
function injectPanelStyles() {
    if (document.getElementById('wa-panel-styles')) return;
    const style = document.createElement('style');
    style.id = 'wa-panel-styles';
    style.textContent = `
        #wa-config-panel {
            position: fixed;
            bottom: 160px;
            right: 28px;
            z-index: 99999;
            display: flex;
            flex-direction: column;
            gap: 12px;
            box-sizing: border-box;
            width: 280px;
            padding: 14px;
            border: 0;
            border-radius: var(--n3i-radius-panel, 12px);
            background: var(--n3i-panel-bg, #1c1e2c);
            box-shadow: var(--n3i-panel-shadow, 0 0 0 1px #3f424d, 0 20px 50px rgba(0, 0, 0, 0.5));
            color: var(--n3i-text, #e9e9ed);
            font: 400 13.5px/1.45 var(--n3i-font, 'Inter', system-ui, sans-serif);
            letter-spacing: normal;
            text-align: left;
            text-transform: none;
            animation: waPanelIn 0.16s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes waPanelIn {
            from { opacity: 0; transform: translateY(-4px); }
            to   { opacity: 1; transform: none; }
        }
        #wa-config-panel :focus-visible { outline: 2px solid var(--n3i-accent, #9184d9); outline-offset: 2px; }
        #wa-config-panel svg { display: block; flex: none; }
        #wa-config-panel .wa-panel-cab { display: flex; align-items: center; gap: 8px; }
        #wa-config-panel .wa-panel-logo { display: flex; flex: none; color: #25D366; }
        #wa-config-panel .wa-panel-logo svg { width: 18px; height: 18px; }
        #wa-config-panel h4 {
            flex: 1;
            min-width: 0;
            margin: 0;
            color: var(--n3i-text, #e9e9ed);
            font: 500 14px/1.3 var(--n3i-font, 'Inter', system-ui, sans-serif);
        }
        #wa-config-panel p {
            margin: 0;
            color: var(--n3i-text-dim, #9397ab);
            font-size: 12px;
            line-height: 1.45;
        }
        #wa-config-panel p strong { color: var(--n3i-text-muted, #b2b6ca); font-weight: 500; }
        #wa-config-panel .wa-input-row {
            display: flex;
            gap: 8px;
        }
        #wa-number-input { flex: 1; min-width: 0; }
        #wa-number-input:focus-visible { outline: none; }          /* ya lo dice el borde */
        #wa-number-input[aria-invalid="true"] { border-color: #f87171; }
        #wa-panel-close {
            position: relative;
            display: flex;
            flex: none;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            margin: 0;
            padding: 0;
            border: 0;
            border-radius: 6px;
            background: transparent;
            color: var(--n3i-text-dim, #9397ab);
            cursor: pointer;
        }
        #wa-panel-close:hover { background: var(--n3i-surface-hover, rgba(233, 233, 237, 0.07)); color: var(--n3i-text, #e9e9ed); }
        #wa-panel-close::before,
        #wa-panel-close::after {                      /* la ✕ (15 px, el trazo del icono «x» del sistema) */
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 13px;
            height: 1.5px;
            border-radius: 1px;
            background: currentColor;
        }
        #wa-panel-close::before { transform: translate(-50%, -50%) rotate(45deg); }
        #wa-panel-close::after { transform: translate(-50%, -50%) rotate(-45deg); }
        @media (prefers-reduced-motion: reduce) { #wa-config-panel { animation: none; } }
    `;
    document.head.appendChild(style);
}

function openPanel(currentNum = '') {
    closePanel();
    injectPanelStyles();

    panelEl = document.createElement('div');
    panelEl.id = 'wa-config-panel';
    panelEl.setAttribute('role', 'dialog');
    panelEl.setAttribute('aria-labelledby', 'wa-panel-titulo');
    // Con destino global de tipo WhatsApp este número NO es solo el del flotante: es el mismo al
    // que apuntan los botones de la web. Decirlo evita que el cliente crea que son dos números.
    const nota = destinoWaDelSitio()
        ? 'Es el mismo número al que llevan los botones de la web (destino del sitio).'
        : 'Número con código de país.';
    panelEl.innerHTML = `
        <div class="wa-panel-cab">
            <span class="wa-panel-logo" aria-hidden="true">${WA_SVG}</span>
            <h4 id="wa-panel-titulo">WhatsApp</h4>
            <button type="button" id="wa-panel-close" aria-label="Cerrar"></button>
        </div>
        <p>${nota}<br>Ej: <strong>34612345678</strong></p>
        <div class="wa-input-row">
            <input id="wa-number-input" class="n3i-campo" type="tel" placeholder="34612345678" aria-label="Número de WhatsApp" autocomplete="off">
            <button type="button" id="wa-save-btn" class="n3i-btn-primary">Guardar</button>
        </div>
    `;
    document.body.appendChild(panelEl);

    // Focus automático. El número va por `.value` (nunca interpolado en el markup).
    const input = panelEl.querySelector('#wa-number-input');
    input.value = currentNum || '';
    input.addEventListener('input', () => input.removeAttribute('aria-invalid'));
    setTimeout(() => input.focus(), 50);

    // Guardar
    panelEl.querySelector('#wa-save-btn').addEventListener('click', saveNumber);
    input.addEventListener('keydown', e => { if (e.key === 'Enter') saveNumber(); });

    // Cerrar (✕ y Esc)
    panelEl.querySelector('#wa-panel-close').addEventListener('click', closePanel);
    document.addEventListener('keydown', escHandler, true);

    // Clic fuera cierra
    setTimeout(() => {
        document.addEventListener('click', outsideClickHandler, { capture: true, once: false });
    }, 100);
}

function outsideClickHandler(e) {
    if (panelEl && !panelEl.contains(e.target) && e.target.id !== 'wa-float-global') {
        closePanel();
        document.removeEventListener('click', outsideClickHandler, true);
    }
}

// Esc cierra el panel (y no sigue hasta el Esc del editor: una cosa por pulsación). El foco, si estaba dentro,
// vuelve al botón flotante que lo abrió.
function escHandler(e) {
    if (e.key !== 'Escape' || !panelEl) return;
    e.stopImmediatePropagation();
    const teniaFoco = panelEl.contains(document.activeElement);
    closePanel();
    const btn = document.getElementById('wa-float-global');
    if (teniaFoco && btn) { try { btn.focus({ preventScroll: true }); } catch (err) { /* sin foco */ } }
}

function closePanel() {
    if (panelEl) { panelEl.remove(); panelEl = null; }
    document.removeEventListener('click', outsideClickHandler, true);
    document.removeEventListener('keydown', escHandler, true);
}

/**
 * ¿El sitio tiene destino global de tipo WhatsApp? Entonces el número vive AHÍ (una sola fuente de
 * verdad: los botones de la web y este flotante marcan el mismo). Sin destino global —o con uno de
 * otro tipo— se mantiene el comportamiento de siempre: el número se guarda en el footer.
 */
function destinoWaDelSitio() {
    const sd = store.get('siteData');
    const d = sd && sd.cta_destino;
    return (d && d.tipo === 'whatsapp') ? d : null;
}

function saveNumber() {
    const input = document.getElementById('wa-number-input');
    if (!input) return;
    const cleanNum = input.value.replace(/\D/g, '');
    if (!cleanNum) { input.focus(); input.setAttribute('aria-invalid', 'true'); return; }

    closePanel();

    // Fuente única: si el número es el del destino global, se actualiza ahí. Pasa por
    // window.guardarCtaDestino (viewer.js) y no por un mutate a pelo porque esa función consolida
    // ANTES las ediciones vivas del DOM: repintar sin eso borraría el texto que el cliente acabara
    // de escribir. El render vuelve a sembrar los data-wa-* del footer desde el destino, así que
    // flotante y botones sin enlace propio quedan con el número nuevo de golpe.
    const destinoWa = destinoWaDelSitio();
    if (destinoWa && window.guardarCtaDestino) {
        window.guardarCtaDestino(Object.assign({}, destinoWa, { valor: cleanNum }));
        return;
    }

    if (window.extraerDatosEditados && store.get('siteBlocks')) {
        const blocks = window.extraerDatosEditados(store.get('siteBlocks'));
        const footerBlock = blocks.find(b => b.type === 'footer');
        if (footerBlock) {
            footerBlock.props.whatsapp = cleanNum;
            store.set('siteBlocks', blocks);
            window.renderPage(blocks);
            return;
        }
    }
}

// ─── BOTÓN FLOTANTE ───────────────────────────────────────────────────────────

function removeExistingWaBtn() {
    const old = document.getElementById('wa-float-global');
    if (old) old.remove();
}

function getFooterWaData() {
    const footer = document.querySelector('footer[data-wa-num]');
    if (!footer) return { num: '', msg: '' };
    return {
        num: footer.getAttribute('data-wa-num') || '',
        msg: footer.getAttribute('data-wa-msg') || ''
    };
}

export function initWaButton() {
    removeExistingWaBtn();
    closePanel();

    const { num, msg } = getFooterWaData();
    const isEditor = store.get('editorMode');

    // Apagado a propósito desde los ajustes: ni siquiera el botón de "Añadir WhatsApp" del editor.
    // Si no, el cliente lo quita y le sigue apareciendo un botón en la esquina invitándole a ponerlo.
    const destinoWa = destinoWaDelSitio();
    if (destinoWa && destinoWa.mostrar_flotante === false) return;

    // Si no hay número configurado y estamos en modo previsualización/sitio real, no mostramos nada
    if (!num && !isEditor) return;

    const btn = document.createElement(num && !isEditor ? 'a' : 'button');
    btn.id = 'wa-float-global';

    if (num && !isEditor) {
        // Preview con número → enlace real a WA
        btn.href = `https://wa.me/${num}?text=${msg}`;
        btn.className = 'wa-float-btn';
        btn.target = '_blank';
        btn.rel = 'noopener';
        btn.title = t('wa_contact');
        btn.innerHTML = WA_SVG;
    } else if (num && isEditor) {
        // Editor con número → botón compacto con icono WA + lápiz, abre panel
        btn.className = 'wa-float-btn wa-float-editable';
        btn.title = 'Editar número de WhatsApp';
        const pencilSvg = `<svg viewBox="0 0 24 24" width="12" height="12" stroke="#fff" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>`;
        btn.innerHTML = WA_SVG + `<span class="wa-edit-badge">${pencilSvg}</span>`;
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (panelEl) { closePanel(); } else { openPanel(num); }
        });
    } else if (!num && isEditor) {
        // Sin número + editor → botón placeholder
        btn.className = 'wa-config-btn';
        btn.title = 'Añadir botón de WhatsApp';
        btn.innerHTML = WA_SVG + `<span style="font-size:0.75rem;font-weight:700;margin-left:6px;">Añadir WhatsApp</span>`;
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (panelEl) { closePanel(); } else { openPanel(''); }
        });
    }

    document.body.appendChild(btn);
}

registerPostRender(initWaButton);
